import type {
  ProductImageArea,
  ProductImageEntry,
  ProductImageScenario,
} from "./types";

interface Words {
  alt: string;
  shows: string;
  proves: string;
}

const entry = (
  id: string,
  module: string,
  opens: string,
  setup: readonly string[],
  sv: Words,
  en: Words,
  options: { area?: ProductImageArea; scenario?: ProductImageScenario } = {},
): ProductImageEntry => ({
  id,
  module,
  area: options.area ?? "schedule",
  opens,
  setup,
  scenario: options.scenario ?? "S1",
  visual: { variant: 0 },
  text: { sv, en },
  use: ["pages/Plattform.tsx"],
});

const SAVED = "Öppna det sparade planeringsresultatet för Nacka CAIRE";
const PAIR =
  "Välj två sparade lösningar för samma planeringsperiod i Jämför lösningar";

/** Planning and compare surfaces captured in the 2026-10 surface campaign. */
export const CAMPAIGN_PLANNING_CATALOGUE: readonly ProductImageEntry[] = [
  entry(
    "compare.chart.visit-assignment",
    "Jämför",
    "Planering → Jämför lösningar → Visuell jämförelse",
    [PAIR, "Läs diagrammet Besökstilldelning"],
    {
      alt: "Diagrammet Besökstilldelning med totala, tilldelade och otilldelade besök för två lösningar",
      shows:
        "Se hur många besök varje lösning tilldelar och lämnar otilldelade.",
      proves:
        "Diagrammet ställer två sparade lösningar för samma period bredvid varandra med totalt antal besök, tilldelade och otilldelade.",
    },
    {
      alt: "Visit assignment chart with total, assigned and unassigned visits for two solutions",
      shows: "See how many visits each solution assigns and leaves unassigned.",
      proves:
        "The chart places two saved solutions for the same period side by side with total, assigned and unassigned visits.",
    },
    { area: "solution" },
  ),
  entry(
    "compare.chart.time-composition",
    "Jämför",
    "Planering → Jämför lösningar → Visuell jämförelse",
    [PAIR, "Läs diagrammet Tidsfördelning"],
    {
      alt: "Diagrammet Tidsfördelning med besökstid, restid, väntetid, betald tomtid och rast för två lösningar",
      shows: "Jämför hur den betalda tiden fördelas i två lösningar.",
      proves:
        "Staplarna delar varje lösnings tid i besökstid, restid, väntetid, betald tomtid och rast, så att skillnaden syns direkt.",
    },
    {
      alt: "Time composition chart with visit, travel, waiting, paid idle and break time for two solutions",
      shows: "Compare how paid time is split in two solutions.",
      proves:
        "The bars split each solution's time into visit, travel, waiting, paid idle and break time, so the difference is visible at once.",
    },
    { area: "solution" },
  ),
  entry(
    "compare.chart.quality-profile",
    "Jämför",
    "Planering → Jämför lösningar → Kvalitetsprofil",
    [PAIR, "Rulla till Kvalitetsprofil"],
    {
      alt: "Kvalitetsprofil som radardiagram för två sparade lösningar",
      shows: "Se två lösningars styrkor på samma axlar.",
      proves:
        "Radardiagrammet jämför tilldelningsgrad, väntejusterad effektivitet, kontinuitet, utnyttjandegrad, reseeffektivitet och vinstmarginal för båda lösningarna.",
    },
    {
      alt: "Quality profile radar chart for two saved solutions",
      shows: "See two solutions' strengths on the same axes.",
      proves:
        "The radar compares assignment rate, wait-aware efficiency, continuity, utilization, travel efficiency and profit margin for both solutions.",
    },
    { area: "solution" },
  ),
  entry(
    "compare.score-panel",
    "Jämför",
    "Planering → Jämför lösningar → Visa poäng & villkorsanalys",
    [PAIR, "Klicka på Visa poäng & villkorsanalys"],
    {
      alt: "Utfälld poäng och villkorsanalys för en lösning i Jämför lösningar",
      shows: "Öppna villkorsanalysen bakom en lösnings poäng.",
      proves:
        "Panelen visar lösningens poäng och hur varje villkor bidrar, direkt i jämförelsen.",
    },
    {
      alt: "Expanded score and constraint analysis for one solution in Compare solutions",
      shows: "Open the constraint analysis behind a solution's score.",
      proves:
        "The panel shows the solution's score and how each constraint contributes, right inside the comparison.",
    },
    { area: "solution" },
  ),
  entry(
    "compare.from-list",
    "Jämför",
    "Planering → Lista → Jämför valda",
    [
      "Öppna schemalistan för Nacka CAIRE",
      "Markera två scheman för jämförelse",
    ],
    {
      alt: "Schemalistan med två markerade scheman och knappen Jämför valda",
      shows: "Markera två scheman i listan och jämför deras bästa lösningar.",
      proves:
        "Kryssrutorna i schemalistan väljer varje schemas bästa lösning, och knappen Jämför valda öppnar jämförelsen.",
    },
    {
      alt: "Schedule list with two selected schedules and the Compare selected button",
      shows:
        "Select two schedules in the list and compare their best solutions.",
      proves:
        "The checkboxes in the schedule list pick each schedule's best solution, and Compare selected opens the comparison.",
    },
    { area: "solution" },
  ),
  entry(
    "planning.solution-picker",
    "Planering",
    "Planering → Kalender → Jämför → Visa 1",
    [SAVED, "Klicka på Jämför"],
    {
      alt: "Lösningsväljaren med sparade lösningar, brukartid, tilldelning och kontinuitet",
      shows: "Välj vilken sparad lösning du vill granska.",
      proves:
        "Väljaren listar schemats sparade lösningar med brukartid, tilldelade besök, kontinuitet och betald tid, så att planeraren kan byta lösning med siffrorna framför sig.",
    },
    {
      alt: "Solution picker with saved solutions, client time, assignment and continuity",
      shows: "Choose which saved solution to review.",
      proves:
        "The picker lists the schedule's saved solutions with client time, assigned visits, continuity and paid time, so the planner switches with the figures in view.",
    },
  ),
  entry(
    "planning.calendar.filter-panel",
    "Planering",
    "Planering → Kalender → Filter",
    [SAVED, "Klicka på Filter"],
    {
      alt: "Kalenderfilter med frekvens, inaktiva skift och typ av händelse",
      shows: "Välj vad kalendern ska visa.",
      proves:
        "Panelen filtrerar kalendern på frekvens och händelsetyp, som besök, resa, paus, väntan och tomgång.",
    },
    {
      alt: "Calendar filters with frequency, inactive shifts and event type",
      shows: "Choose what the calendar shows.",
      proves:
        "The panel filters the calendar by frequency and event type, such as visit, travel, break, waiting and idle time.",
    },
  ),
  entry(
    "planning.calendar.client-filter-panel",
    "Planering",
    "Planering → Kalender → Kund → Filter",
    [SAVED, "Byt till kundrader", "Klicka på Filter"],
    {
      alt: "Kundfilter med insats, insatsgrupp och frekvens i kundvyn",
      shows: "Filtrera kundvyn på insatser, insatsgrupper och frekvens.",
      proves:
        "I kundvyn filtrerar panelen raderna på insats, insatsgrupp, frekvens, medarbetare och beroenden.",
    },
    {
      alt: "Client filters with service type, service group and frequency in the client view",
      shows:
        "Filter the client view by service type, service group and frequency.",
      proves:
        "In the client view the panel filters rows by service type, service group, frequency, employee and dependencies.",
    },
  ),
  entry(
    "planning.calendar.compare-panel",
    "Planering",
    "Planering → Kalender → Jämför",
    [SAVED, "Klicka på Jämför"],
    {
      alt: "Panelen Jämför med reglage för tilldelat och brukartid och en lista med lösningar",
      shows:
        "Hitta en bättre lösning för samma vecka utan att lämna kalendern.",
      proves:
        "Panelen visar den öppna lösningen mot schemats övriga lösningar med brukartid, tilldelat, kontinuitet och betald tid.",
    },
    {
      alt: "Compare panel with assigned and client-time sliders and a list of solutions",
      shows:
        "Find a better solution for the same week without leaving the calendar.",
      proves:
        "The panel sets the open solution against the schedule's other solutions with client time, assigned, continuity and paid time.",
    },
  ),
  entry(
    "planning.calendar.comparison-stack",
    "Planering",
    "Planering → Kalender → Jämför → Visa 2",
    [SAVED, "Klicka på Jämför", "Välj en andra lösning under Visa 2"],
    {
      alt: "Två kalendrar för samma dag staplade över varandra",
      shows: "Se två lösningar för samma dag i två kalendrar.",
      proves:
        "Med Visa 2 visas en andra sparad lösning i en egen kalender under den första, för samma datum.",
    },
    {
      alt: "Two calendars for the same day stacked above each other",
      shows: "See two solutions for the same day in two calendars.",
      proves:
        "With View 2 a second saved solution appears in its own calendar below the first, for the same date.",
    },
  ),
  entry(
    "planning.scheduling-home",
    "Översikt",
    "Översikt",
    ["Välj enheten Nacka CAIRE i Översikt"],
    {
      alt: "Översikten med schemastatus, grunddata och förslag på vad planeraren gör idag",
      shows: "Se läget för enheten och nästa steg på ett ställe.",
      proves:
        "Översikten visar publicerade scheman, utkast, pågående optimeringar och ärenden, tillsammans med enhetens grunddata och genvägar till dagens arbete.",
    },
    {
      alt: "Overview with schedule status, standing data and suggestions for the planner's day",
      shows: "See the unit's situation and next steps in one place.",
      proves:
        "The overview shows published schedules, drafts, running optimizations and open items, with the unit's standing data and shortcuts to today's work.",
    },
  ),
  entry(
    "planning.home.intent-panel",
    "Översikt",
    "Översikt → Vad vill ni göra idag?",
    ["Välj enheten Nacka CAIRE i Översikt"],
    {
      alt: "Panelen Vad vill ni göra idag? med ett rekommenderat kort",
      shows: "Börja med det som läget kräver just nu.",
      proves:
        "Panelen föreslår planerarens vanligaste uppgifter och markerar den som passar läget, till exempel att granska väntande utkast.",
    },
    {
      alt: "The What do you want to do today? panel with a recommended card",
      shows: "Start with what the situation needs right now.",
      proves:
        "The panel offers the planner's most common tasks and marks the one that fits the situation, such as reviewing waiting drafts.",
    },
  ),
  entry(
    "planning.home.continuity-kpi",
    "Översikt",
    "Översikt → Kontinuitet och kundsignal",
    [
      "Välj enheten Nacka CAIRE i Översikt",
      "Rulla till Kontinuitet och kundsignal",
    ],
    {
      alt: "Kontinuitet och kundsignal med nyckeltal för enheten",
      shows: "Följ kontinuiteten för enhetens kunder.",
      proves:
        "Panelen visar hur många medarbetare kunderna i snitt möter och lyfter fram kunder där kontinuiteten behöver bevakas.",
    },
    {
      alt: "Continuity and client signal with key figures for the unit",
      shows: "Follow continuity for the unit's clients.",
      proves:
        "The panel shows how many caregivers clients meet on average and highlights clients whose continuity needs attention.",
    },
  ),
  entry(
    "planning.schedules-list",
    "Planering",
    "Planering → Lista",
    ["Öppna schemalistan för Nacka CAIRE"],
    {
      alt: "Schemalistan med enhetens scheman, status och nyckeltal",
      shows: "Se alla scheman för enheten i en lista.",
      proves:
        "Listan visar varje schema med period, status och nyckeltal för bästa lösningen, med filter och jämförelse i samma vy.",
    },
    {
      alt: "Schedule list with the unit's schedules, status and key figures",
      shows: "See all of the unit's schedules in one list.",
      proves:
        "The list shows each schedule with period, status and key figures for its best solution, with filters and comparison in the same view.",
    },
  ),
  entry(
    "planning.schedules-list.facet-panel",
    "Planering",
    "Planering → Lista → Filter",
    ["Öppna schemalistan för Nacka CAIRE"],
    {
      alt: "Filterpanelen i schemalistan med status, typ och datum",
      shows: "Smalna av listan på status, typ och datum.",
      proves:
        "Filterpanelen begränsar schemalistan på status, schematyp och datumintervall.",
    },
    {
      alt: "Filter panel in the schedule list with status, type and date",
      shows: "Narrow the list by status, type and date.",
      proves:
        "The filter panel limits the schedule list by status, schedule type and date range.",
    },
  ),
  entry(
    "planning.schedules-list.empty",
    "Planering",
    "Planering → Lista (enhet utan scheman)",
    ["Välj en enhet som ännu saknar scheman"],
    {
      alt: "Tom schemalista med vägledning om vilken grunddata som saknas",
      shows: "Se vad som behövs innan det första schemat.",
      proves:
        "När enheten saknar scheman visar listan vilken grunddata som fattas och leder vidare till Kom igång-guiden eller rapportuppladdning.",
    },
    {
      alt: "Empty schedule list with guidance on missing standing data",
      shows: "See what is needed before the first schedule.",
      proves:
        "When the unit has no schedules the list shows which standing data is missing and leads to the getting-started guide or report upload.",
    },
  ),
  entry(
    "planning.calendar-entry",
    "Planering",
    "Planering → Kalender (dag utan plan)",
    ["Välj Nacka CAIRE och en dag utan planering"],
    {
      alt: "Planeringskalendern för en dag utan plan, med veckoremsa och Skapa plan",
      shows: "Se vilka veckor som är planerade och skapa en plan för resten.",
      proves:
        "Kalendern visar åtta veckor framåt med status per vecka, och för en dag utan planering finns Skapa plan direkt i verktygsfältet.",
    },
    {
      alt: "Planning calendar for a day without a plan, with the week strip and Create plan",
      shows: "See which weeks are planned and create a plan for the rest.",
      proves:
        "The calendar shows eight weeks ahead with a status per week, and for a day without a plan Create plan sits right in the toolbar.",
    },
  ),
  entry(
    "planning.continuous.date-timeline",
    "Planering",
    "Planering → Kalender → veckoremsan",
    ["Välj Nacka CAIRE och en dag utan planering"],
    {
      alt: "Veckoremsan med planeringsstatus för åtta veckor",
      shows: "Hoppa mellan veckor och se vilka som har en plan.",
      proves:
        "Remsan visar varje vecka med status, som Publicerad, Utkast eller Planering saknas.",
    },
    {
      alt: "Week strip with planning status for eight weeks",
      shows: "Jump between weeks and see which have a plan.",
      proves:
        "The strip shows each week with its status, such as Published, Draft or Plan missing.",
    },
  ),
  entry(
    "planning.empty-schedule-workspace",
    "Planering",
    "Planering → Kalender (enhet utan schema)",
    ["Välj en enhet som ännu saknar scheman"],
    {
      alt: "Dagsschema utan schema för vald enhet, med grunddata som krävs",
      shows: "Se varför kalendern är tom och vad som krävs.",
      proves:
        "Kalendern visar att enheten saknar schema och listar grunddatan som behövs för att skapa ett.",
    },
    {
      alt: "Daily schedule without a schedule for the unit, with the required standing data",
      shows: "See why the calendar is empty and what is required.",
      proves:
        "The calendar shows that the unit has no schedule and lists the standing data needed to create one.",
    },
  ),
  entry(
    "planning.details.bottleneck-analysis",
    "Planering",
    "Planering → Kalender → Nyckeltal → Flaskhalsanalys",
    [SAVED, "Öppna Nyckeltal och fäll ut Flaskhalsanalys"],
    {
      alt: "Flaskhalsanalys med värmekarta över timmar och dagar",
      shows: "Hitta timmarna där besöken inte får plats.",
      proves:
        "Flaskhalsanalysen visar per dag och timme var efterfrågan överstiger bemanningen i den valda lösningen.",
    },
    {
      alt: "Bottleneck analytics with a heatmap of hours and days",
      shows: "Find the hours where visits do not fit.",
      proves:
        "Bottleneck analytics shows per day and hour where demand exceeds staffing in the selected solution.",
    },
  ),
  entry(
    "planning.details.unassigned-by-inset",
    "Planering",
    "Planering → Kalender → Nyckeltal → Flaskhalsanalys",
    [SAVED, "Öppna Nyckeltal och fäll ut Flaskhalsanalys"],
    {
      alt: "Otilldelade besök per insatstyp med tid och antal",
      shows: "Se vilka insatser som blir otilldelade.",
      proves:
        "Diagrammet delar de otilldelade besöken per insatstyp med tid och antal, och markerar om de är fasta eller flexibla.",
    },
    {
      alt: "Unassigned visits by service type with time and count",
      shows: "See which services end up unassigned.",
      proves:
        "The chart splits unassigned visits by service type with time and count, and marks whether they are fixed or flexible.",
    },
  ),
  entry(
    "planning.details.demand-bucket-drilldown",
    "Planering",
    "Planering → Kalender → Efterfrågan → välj ett intervall",
    [SAVED, "Öppna Efterfrågan och välj ett täckningsintervall"],
    {
      alt: "Besök i tidsfönstret med totalt, tilldelade och otilldelade besök",
      shows: "Öppna ett intervall och se besöken bakom staplarna.",
      proves:
        "Panelen listar besöken i det valda intervallet med kund, insats, längd och tilldelad medarbetare.",
    },
    {
      alt: "Visits in the time window with total, assigned and unassigned visits",
      shows: "Open an interval and see the visits behind the bars.",
      proves:
        "The panel lists the visits in the selected interval with client, service, duration and assigned caregiver.",
    },
  ),
  entry(
    "planning.visits-list",
    "Planering",
    "Planering → Besök",
    ["Öppna Besök för Nacka CAIRE", "Välj Alla"],
    {
      alt: "Besökslistan med kund, insats, datum och tider",
      shows: "Sök och granska alla besök i enhetens scheman.",
      proves:
        "Listan visar besöken i enhetens scheman med kund, insats, besöksgrupp, datum och tider, och kan sökas och filtreras.",
    },
    {
      alt: "Visit list with client, service, date and times",
      shows: "Search and review all visits in the unit's schedules.",
      proves:
        "The list shows the visits in the unit's schedules with client, service, visit group, date and times, and can be searched and filtered.",
    },
  ),
  entry(
    "planning.visits-list.empty",
    "Planering",
    "Planering → Besök (enhet utan besök)",
    ["Öppna Besök för en enhet utan scheman"],
    {
      alt: "Tom besökslista med förklaring och genvägar",
      shows: "Förstå varför listan är tom.",
      proves:
        "När inga besök finns för perioden förklarar listan varför och leder till alla perioder eller besöksmallarna.",
    },
    {
      alt: "Empty visit list with an explanation and shortcuts",
      shows: "Understand why the list is empty.",
      proves:
        "When no visits exist for the period the list explains why and leads to all periods or the visit templates.",
    },
  ),
  entry(
    "planning.shifts-list",
    "Planering",
    "Planering → Skift",
    ["Öppna Skift för schemat i Nacka CAIRE"],
    {
      alt: "Skiftlistan med personal, skifttyp, datum, tider och rast",
      shows: "Se alla skift i ett schema.",
      proves:
        "Listan visar schemats skift med medarbetare, skifttyp, datum, start, slut, längd och rast.",
    },
    {
      alt: "Shift list with staff, shift type, date, times and break",
      shows: "See all shifts in a schedule.",
      proves:
        "The list shows the schedule's shifts with caregiver, shift type, date, start, end, length and break.",
    },
  ),
  entry(
    "planning.create-wizard",
    "Planering",
    "Översikt → Skapa nytt schema",
    ["Öppna Skapa nytt schema och välj enheten Nacka Syd och vecka 45"],
    {
      alt: "Dialogen Nytt schema med enhet, period och inställningar",
      shows: "Skapa ett schema för en enhet och period.",
      proves:
        "Dialogen samlar enhet, period, insatsflex, transport och förutsättningar innan schemat skapas.",
    },
    {
      alt: "New schedule dialog with unit, period and settings",
      shows: "Create a schedule for a unit and period.",
      proves:
        "The dialog gathers unit, period, visit flex, transport and readiness before the schedule is created.",
    },
  ),
  entry(
    "planning.create-wizard.transport",
    "Planering",
    "Översikt → Skapa nytt schema → Transport",
    ["Öppna Skapa nytt schema och välj enheten Nacka Syd och vecka 45"],
    {
      alt: "Transportpanelen i Nytt schema med färdsätt för personalen",
      shows: "Kontrollera färdsätt innan schemat skapas.",
      proves:
        "Panelen visar vilka färdsätt personalen har så att restiden räknas rätt i schemat.",
    },
    {
      alt: "Transport panel in New schedule with staff travel modes",
      shows: "Check travel modes before creating the schedule.",
      proves:
        "The panel shows the staff's travel modes so travel time is calculated correctly in the schedule.",
    },
  ),
  entry(
    "planning.create-wizard.insatsflex",
    "Planering",
    "Översikt → Skapa nytt schema → Insatsflex",
    ["Öppna Skapa nytt schema och välj enheten Nacka Syd och vecka 45"],
    {
      alt: "Insatsflex och skiftflex i Nytt schema",
      shows: "Välj hur mycket besök och skift får flyttas.",
      proves:
        "Panelen visar vilken insatsflex och skiftflex schemat skapas med, innan något sparas.",
    },
    {
      alt: "Visit flex and shift flex in New schedule",
      shows: "Choose how far visits and shifts may move.",
      proves:
        "The panel shows the visit flex and shift flex the schedule will be created with, before anything is saved.",
    },
  ),
  entry(
    "planning.create-wizard.readiness",
    "Planering",
    "Översikt → Skapa nytt schema → Förutsättningar",
    ["Öppna Skapa nytt schema och välj enheten Nacka Syd och vecka 45"],
    {
      alt: "Förutsättningar för att skapa schemat i Nytt schema",
      shows: "Se om underlaget räcker innan schemat skapas.",
      proves:
        "Panelen visar vilka förutsättningar som är uppfyllda och vad som behöver åtgärdas för perioden.",
    },
    {
      alt: "Schedule creation readiness in New schedule",
      shows: "See whether the inputs are sufficient before creating.",
      proves:
        "The panel shows which prerequisites are met and what needs fixing for the period.",
    },
  ),
  entry(
    "planning.create-wizard.preflight-analytics",
    "Planering",
    "Översikt → Skapa nytt schema → Efterfrågan",
    ["Öppna Skapa nytt schema och välj enheten Nacka Syd och vecka 45"],
    {
      alt: "Efterfrågan före optimering i Nytt schema",
      shows: "Se periodens efterfrågan innan optimeringen startar.",
      proves:
        "Panelen visar besöksbehovet för perioden och vad som ingår i underlaget innan schemat optimeras.",
    },
    {
      alt: "Demand before optimization in New schedule",
      shows: "See the period's demand before optimization starts.",
      proves:
        "The panel shows the period's visit demand and what the input includes before the schedule is optimized.",
    },
  ),
  entry(
    "planning.create-wizard.frontier-launch",
    "Planering",
    "Översikt → Skapa nytt schema → Optimera",
    ["Öppna Skapa nytt schema och välj enheten Nacka Syd och vecka 45"],
    {
      alt: "Start av optimering med alternativ i Nytt schema",
      shows: "Starta optimeringen och få flera alternativ att välja mellan.",
      proves:
        "Från dialogen startar planeraren optimeringen och får alternativa lösningar att jämföra innan något publiceras.",
    },
    {
      alt: "Launch optimization with alternatives in New schedule",
      shows: "Start optimization and get several alternatives to choose from.",
      proves:
        "From the dialog the planner starts optimization and gets alternative solutions to compare before anything is published.",
    },
  ),
  entry(
    "planning.create-wizard.recovery",
    "Planering",
    "Översikt → Skapa nytt schema → Spara utan optimering",
    ["Öppna Skapa nytt schema och välj enheten Nacka Syd och vecka 45"],
    {
      alt: "Spara utan optimering i Nytt schema",
      shows: "Spara schemat nu och optimera senare.",
      proves:
        "Planeraren kan spara schemat utan att optimera och fortsätta senare från samma underlag.",
    },
    {
      alt: "Save without optimization in New schedule",
      shows: "Save the schedule now and optimize later.",
      proves:
        "The planner can save the schedule without optimizing and continue later from the same input.",
    },
  ),
  entry(
    "planning.publication-header",
    "Planering",
    "Planering → Kalender → Publicera",
    [SAVED, "Klicka på Publicera"],
    {
      alt: "Bekräftelsen Publicera lösning? med vad publiceringen innebär",
      shows: "Se vad som händer innan du publicerar en lösning.",
      proves:
        "Innan lösningen publiceras förklarar Caire att den blir schemat som medarbetare och kunder ser, ersätter en tidigare publicerad lösning och låser schemat.",
    },
    {
      alt: "The Publish solution? confirmation with what publishing means",
      shows: "See what happens before you publish a solution.",
      proves:
        "Before the solution is published, Caire explains that it becomes the schedule staff and clients see, replaces any earlier published solution and locks the schedule.",
    },
  ),
  entry(
    "planning.manual-baseline-banner",
    "Planering",
    "Planering → Kalender (manuell utgångsplan)",
    ["Öppna en sparad utgångsplan utan rutt i kalendern"],
    {
      alt: "Besked om manuell utgångsplan utan rutt i kalendern",
      shows: "Förstå att planen ännu saknar beräknade rutter.",
      proves:
        "Banderollen visar att lösningen är en manuell utgångsplan utan ruttoptimering och leder vidare till nästa steg.",
    },
    {
      alt: "Notice about a manual baseline without routes in the calendar",
      shows: "Understand that the plan has no calculated routes yet.",
      proves:
        "The banner shows that the solution is a manual baseline without route optimization and leads to the next step.",
    },
  ),
  entry(
    "planning.optimize-sheet",
    "Planering",
    "Planering → Kalender → Optimera",
    ["Öppna utkastet i kalendern", "Klicka på Optimera"],
    {
      alt: "Panelen Re-optimera med val för en ny optimering av det öppna utkastet",
      shows: "Starta en ny optimering från kalendern.",
      proves:
        "Panelen samlar valen för en ny optimering av den öppna lösningen innan något skickas.",
    },
    {
      alt: "Re-optimize panel with choices for a new optimization of the open draft",
      shows: "Start a new optimization from the calendar.",
      proves:
        "The panel gathers the choices for a new optimization of the open solution before anything is sent.",
    },
  ),

  entry(
    "planning.employee-removal-dialog",
    "Planering",
    "Planering → Kalender → medarbetarrad → Ta bort från denna lösning",
    [
      "Öppna utkastet i kalendern",
      "Högerklicka på en medarbetare och välj Ta bort från denna lösning",
    ],
    {
      alt: "Bekräftelse innan en medarbetare tas bort från lösningen",
      shows: "Ta bort en medarbetare från lösningen med en tydlig bekräftelse.",
      proves:
        "Dialogen förklarar vad som påverkas och kräver bekräftelse innan medarbetaren tas bort.",
    },
    {
      alt: "Confirmation before a caregiver is removed from the solution",
      shows: "Remove a caregiver from the solution with a clear confirmation.",
      proves:
        "The dialog explains what is affected and asks for confirmation before the caregiver is removed.",
    },
  ),
  entry(
    "planning.visit-draft-dialog",
    "Planering",
    "Planering → Kalender → Karta → Föreslå ändring",
    [
      "Öppna utkastet på kartan",
      "Välj ett pass och ett besök",
      "Klicka på Föreslå ändring för valt besök",
    ],
    {
      alt: "Dialogen Föreslå ändring för ett besök i utkastet",
      shows:
        "Föreslå en ändring av ett besök utan att röra den sparade lösningen.",
      proves:
        "Ändringen läggs i ett utkast som planeraren granskar innan en ny lösning sparas.",
    },
    {
      alt: "Propose change dialog for a visit in the draft",
      shows: "Propose a change to a visit without touching the saved solution.",
      proves:
        "The change goes into a draft that the planner reviews before a new solution is saved.",
    },
  ),
  entry(
    "planning.draft-review",
    "Planering",
    "Planering → Kalender → Karta → Planerade ändringar",
    ["Öppna utkastet på kartan", "Fäll ut Planerade ändringar"],
    {
      alt: "Planerade ändringar under kartan",
      shows: "Samla och granska föreslagna ändringar på ett ställe.",
      proves:
        "Panelen visar ändringarna i utkastet innan de sparas som en ny lösning.",
    },
    {
      alt: "Planned changes under the map",
      shows: "Collect and review proposed changes in one place.",
      proves:
        "The panel shows the draft's changes before they are saved as a new solution.",
    },
  ),
  entry(
    "planning.continuous.workspace",
    "Planering",
    "Planering (löpande planering)",
    ["Öppna Planering för Nacka CAIRE med löpande planering"],
    {
      alt: "Arbetsytan för löpande planering med åtta veckor framåt",
      shows: "Planera vecka för vecka i en sammanhängande horisont.",
      proves:
        "Arbetsytan visar planeringshorisonten, den valda veckans version och verktygen för att jämföra, granska ändringar och ställa in planeringen.",
    },
    {
      alt: "Continuous planning workspace with eight weeks ahead",
      shows: "Plan week by week across one continuous horizon.",
      proves:
        "The workspace shows the planning horizon, the selected week's version and the tools to compare, review changes and configure planning.",
    },
  ),
  entry(
    "planning.continuous.timeline",
    "Planering",
    "Planering (löpande planering) → Filter",
    ["Öppna Planering med löpande planering", "Öppna Filter"],
    {
      alt: "Planeringshorisonten med veckor och deras status",
      shows: "Se alla veckor i horisonten och deras läge.",
      proves: "Horisonten listar veckorna med status och kan filtreras.",
    },
    {
      alt: "Planning horizon with weeks and their status",
      shows: "See every week in the horizon and its state.",
      proves:
        "The horizon lists the weeks with their status and can be filtered.",
    },
  ),
  entry(
    "planning.continuous.alternatives",
    "Planering",
    "Planering (löpande planering) → Jämför",
    ["Öppna Planering med löpande planering", "Öppna Jämför"],
    {
      alt: "Sparade alternativ för den valda veckan",
      shows: "Jämför de alternativ som sparats för veckan.",
      proves:
        "Panelen visar veckans sparade alternativ och vilken version som gäller.",
    },
    {
      alt: "Retained alternatives for the selected week",
      shows: "Compare the alternatives kept for the week.",
      proves:
        "The panel shows the week's retained alternatives and which version applies.",
    },
  ),
  entry(
    "planning.continuous.changes",
    "Planering",
    "Planering (löpande planering) → Ändringar",
    ["Öppna Planering med löpande planering", "Öppna Ändringar"],
    {
      alt: "Planeringsändringar för den valda veckan",
      shows: "Se vad som har ändrats i underlaget för veckan.",
      proves:
        "Panelen listar ändringar i underlaget som påverkar veckans planering.",
    },
    {
      alt: "Planning changes for the selected week",
      shows: "See what has changed in the inputs for the week.",
      proves: "The panel lists input changes that affect the week's planning.",
    },
  ),
  entry(
    "planning.continuous.policy-form",
    "Planering",
    "Planering (löpande planering) → Inställningar",
    ["Öppna Planering med löpande planering", "Öppna Inställningar"],
    {
      alt: "Inställningar för löpande planering",
      shows: "Ställ in hur den löpande planeringen ska arbeta.",
      proves:
        "Formuläret styr planeringshorisonten och hur veckorna hålls uppdaterade.",
    },
    {
      alt: "Continuous planning settings",
      shows: "Set how continuous planning works.",
      proves:
        "The form controls the planning horizon and how the weeks are kept up to date.",
    },
  ),
  entry(
    "planning.validate-result-dialog",
    "Planering",
    "Planering → Kalender → Validera",
    ["Öppna utkastet i kalendern", "Klicka på Validera och bekräfta"],
    {
      alt: "Valideringsresultat för den öppna lösningen",
      shows: "Kontrollera lösningen mot reglerna innan den publiceras.",
      proves:
        "Valideringen räknar om lösningens poäng mot planeringsreglerna och visar resultatet utan att ändra lösningen.",
    },
    {
      alt: "Validation result for the open solution",
      shows: "Check the solution against the rules before publishing.",
      proves:
        "Validation recalculates the solution's score against the planning rules and shows the result without changing the solution.",
    },
  ),
  entry(
    "planning.save-as-solution-dialog",
    "Planering",
    "Planering → Kalender → Spara utkast",
    [
      "Öppna utkastet i kalendern",
      "Flytta ett besök",
      "Klicka på Spara utkast",
    ],
    {
      alt: "Dialogen Spara som ny lösning efter en ändring i kalendern",
      shows: "Spara dina ändringar som en ny lösning.",
      proves:
        "Ändringar i kalendern sparas som en ny lösning; den ursprungliga lösningen finns kvar oförändrad.",
    },
    {
      alt: "Save as new solution dialog after a change in the calendar",
      shows: "Save your changes as a new solution.",
      proves:
        "Calendar changes are saved as a new solution; the original solution stays unchanged.",
    },
  ),
  entry(
    "planning.optimization-starting-banner",
    "Planering",
    "Översikt → Skapa nytt schema → Skapa och optimera schema",
    [
      "Öppna Skapa nytt schema för enheten Nacka Syd och vecka 46",
      "Klicka på Skapa och optimera schema",
    ],
    {
      alt: "Beskedet Optimeringen startar över kalendern medan ruttförslagen beräknas",
      shows: "Följ optimeringen direkt i kalendern efter att schemat skapats.",
      proves:
        "Medan rutterna beräknas visar kalendern medarbetarnas pass och ett besked om att optimeringen pågår; den första färdiga ruttlösningen öppnas automatiskt.",
    },
    {
      alt: "The Optimization is starting notice above the calendar while route proposals are calculated",
      shows:
        "Follow the optimization in the calendar right after the schedule is created.",
      proves:
        "While routes are calculated, the calendar shows the employees' shifts and a notice that optimization is running; the first finished route solution opens automatically.",
    },
  ),
  entry(
    "planning.ess-solving-banner",
    "Planering",
    "Planering → Schema (skiftplanering pågår)",
    [
      "Skapa och optimera ett schema för enheten Nacka Syd",
      "Öppna schemats sida medan skiften tas fram",
    ],
    {
      alt: "Beskedet att skiften genereras på schemats sida medan skiftplaneringen pågår",
      shows: "Se att skiftplaneringen körs innan rutterna planeras.",
      proves:
        "Schemats sida visar när skiftplaneringen pågår och att ruttplaneringen startar automatiskt när skiften är klara.",
    },
    {
      alt: "Notice that shifts are being generated on the schedule page while shift planning runs",
      shows: "See that shift planning is running before routes are planned.",
      proves:
        "The schedule page shows when shift planning is running and that route planning starts automatically once the shifts are ready.",
    },
  ),
  entry(
    "planning.from-patch-summary",
    "Planering",
    "Planering → Kalender → Mätvärden (lösning skapad från en ändring)",
    [
      "Validera den sparade ruttlösningen för Nacka CAIRE",
      "Öppna den validerade lösningen och välj Mätvärden",
    ],
    {
      alt: "Sammanfattning av vad som ändrats jämfört med ursprungslösningen i panelen Mätvärden",
      shows: "Se vad en ny lösning ändrade jämfört med den den skapades från.",
      proves:
        "En lösning som skapats från en annan visar skillnaden mot ursprunget och länkar till en jämförelse av de två.",
    },
    {
      alt: "Summary of what changed compared with the original solution in the Metrics panel",
      shows:
        "See what a new solution changed compared with the one it was created from.",
      proves:
        "A solution created from another shows the difference against its origin and links to a comparison of the two.",
    },
  ),
  entry(
    "planning.dependency-edit-dialog",
    "Planering",
    "Planering → Kalender → Kund → beroendepil",
    [
      "Öppna kalendern för vecka 44 med raderna per kund",
      "Sök fram kunden och dubbelklicka på pilen mellan två besök",
    ],
    {
      alt: "Dialogen Redigera beroende med fördröjning och samordning mellan två besök hos samma kund",
      shows: "Ändra hur två besök hos samma kund hänger ihop.",
      proves:
        "Beroendet mellan två besök i kundens plan går att redigera direkt från kalendern, med fördröjning och samordning, eller tas bort.",
    },
    {
      alt: "The Edit dependency dialog with delay and coordination between two visits for the same client",
      shows: "Change how two visits for the same client are linked.",
      proves:
        "The dependency between two visits in the client's plan can be edited straight from the calendar, with delay and coordination, or removed.",
    },
  ),
  entry(
    "planning.feedback-loop-banner",
    "Planering",
    "Planering → Schema (lösningen slår inte utgångsläget)",
    [
      "Ange enhetens manuella utgångsläge för Nacka Syd",
      "Öppna schemat för vecka 37",
    ],
    {
      alt: "Beskedet Lösningen kan förbättras med knappen Visa Hälsokontroll på schemats sida",
      shows:
        "Se när den optimerade lösningen ännu inte är bättre än ert utgångsläge.",
      proves:
        "Schemats sida jämför den senaste ruttlösningen med enhetens manuella utgångsläge och leder till Hälsokontroll när lösningen inte är bättre på minst två av tre nyckeltal.",
    },
    {
      alt: "The solution can be improved notice with the View Health Check button on the schedule page",
      shows:
        "See when the optimized solution is not yet better than your starting point.",
      proves:
        "The schedule page compares the latest route solution with the unit's manual baseline and leads to Health Check when the solution is not better on at least two of three key figures.",
    },
  ),
  entry(
    "planning.flex-select-publish",
    "Planering",
    "Planering → Schema → förslag att publicera",
    ["Öppna schemat för vecka 44 efter en optimering med flera förslag"],
    {
      alt: "Panelen med det rekommenderade förslaget att publicera på schemats sida",
      shows: "Välj det förslag som optimeringen rekommenderar.",
      proves:
        "Efter en optimering med flera förslag visar schemats sida det rekommenderade förslaget med dess nyckeltal, redo att väljas.",
    },
    {
      alt: "The panel with the recommended proposal to publish on the schedule page",
      shows: "Choose the proposal the optimization recommends.",
      proves:
        "After an optimization with several proposals, the schedule page shows the recommended proposal with its key figures, ready to be chosen.",
    },
  ),
  entry(
    "planning.overlap-decision-dialog",
    "Planering",
    "Planering → Kalender → Luckor → dra ett otilldelat besök",
    [
      "Öppna kalendern för vecka 44 med vyn Luckor",
      "Dra ett otilldelat besök till medarbetaren som redan besöker samma kund vid samma tid",
    ],
    {
      alt: "Panelen Dubbelbemanning för samma klient med besöket som flyttas och det befintliga besöket",
      shows: "Bestäm vad som ska gälla när två besök hos samma kund krockar.",
      proves:
        "När ett otilldelat besök läggs på samma tid som ett annat besök hos samma kund frågar kalendern om besöken ska grupperas som dubbel eller tillåtas överlappa, innan något ändras.",
    },
    {
      alt: "The Double-staffing for same client panel with the visit being moved and the existing visit",
      shows: "Decide what applies when two visits for the same client collide.",
      proves:
        "When an unassigned visit is dropped on the same time as another visit for the same client, the calendar asks whether to group them as a dubbel or allow the overlap, before anything changes.",
    },
  ),
];
